// LAP-646 — Card "Relations clés" : Top 3 actives + 3 à réactiver
import React from 'react'
import { Box, Stack, Avatar } from '@mui/material'
import { useTranslation } from 'react-i18next'
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import moment from 'moment'
import CardHeader from '../../Investments/_shared/CardHeader'
import EmptyHelp from './EmptyHelp'
// LAP-XXX — Social Dashboard v2 : alignement design tokens (4 buckets canoniques).
import { RELATIONSHIP_COLORS } from '../../MesContacts/_shared/RelationshipFields'

const BUCKET_PALETTE = {
  ...RELATIONSHIP_COLORS,
  // Compat ascendante au cas où l'API renverrait encore d'anciens labels.
  famille: RELATIONSHIP_COLORS.family,
  amis: RELATIONSHIP_COLORS.friendship,
  amour: RELATIONSHIP_COLORS.intimacy,
  autres: '#94a3b8',
}

function getInitials(name) {
  if (!name) return '?'
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
}

function PersonRow({ person, badge, accent }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        p: 1,
        borderRadius: 1.5,
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
        transition: 'background 150ms ease',
      }}
    >
      <Avatar
        src={person.picture_link || undefined}
        sx={{
          width: 36,
          height: 36,
          mr: 1.25,
          backgroundColor: BUCKET_PALETTE[person.bucket] || '#94a3b8',
          color: '#001b42',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {getInitials(person.full_name)}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontWeight: 600,
            fontSize: 13.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {person.full_name}
        </Box>
        <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{badge}</Box>
      </Box>
      <Box
        sx={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: BUCKET_PALETTE[person.bucket] || '#94a3b8',
          backgroundColor: `${BUCKET_PALETTE[person.bucket] || '#94a3b8'}22`,
          px: 0.75,
          py: 0.25,
          borderRadius: 999,
        }}
      >
        {person.bucket}
      </Box>
    </Box>
  )
}

export default function KeyRelationsCard({ data, loading }) {
  const { t } = useTranslation('layout')
  const top = data?.top_active || []
  const reac = data?.to_reactivate || []
  if (!loading && top.length === 0 && reac.length === 0) {
    return (
      <Box display="flex" flexDirection="column" height="100%">
        <CardHeader
          icon={<StarOutlineOutlinedIcon fontSize="small" />}
          title={t('social.key.title', 'Relations clés')}
          subtitle={t('social.key.subtitle', 'Top actives · À réactiver')}
        />
        <EmptyHelp
          icon={<StarOutlineOutlinedIcon sx={{ fontSize: 22 }} />}
          title={t('social.empty.key.title', 'Pas encore de relations clés')}
          description={t(
            'social.empty.key.description',
            'Le Top 3 sort des contacts avec qui tu interagis le plus, et le bloc « à réactiver » repère ceux que tu n’as pas vus depuis longtemps. Ajoute des contacts et lie-les à des événements du calendrier pour les faire apparaître ici.',
          )}
          actions={[
            { key: 'contacts', label: t('social.empty.cta.addContact', 'Ajouter un contact') },
            { key: 'calendar', label: t('social.empty.cta.createEvent', 'Créer un événement') },
          ]}
        />
      </Box>
    )
  }
  return (
    <Box display="flex" flexDirection="column" height="100%">
      <CardHeader
        icon={<StarOutlineOutlinedIcon fontSize="small" />}
        title={t('social.key.title', 'Relations clés')}
        subtitle={t('social.key.subtitle', 'Top actives · À réactiver')}
      />
      <Box mt={2} sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1,
            color: '#34d399',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontWeight: 600,
          }}
        >
          <StarOutlineOutlinedIcon sx={{ fontSize: 14 }} />
          {t('social.key.topActive', 'Top 3 relations actives')}
        </Box>
        <Stack spacing={0.75} mb={2}>
          {top.length > 0 ? (
            top.map((p) => (
              <PersonRow
                key={`top-${p.id}`}
                person={p}
                badge={`${p.interactions} ${t('social.key.interactions', 'interactions')}`}
              />
            ))
          ) : (
            <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
              {t('social.key.noTop', 'Aucune interaction récente')}
            </Box>
          )}
        </Stack>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1,
            color: '#f59e0b',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontWeight: 600,
            borderTop: '1px dashed rgba(255,255,255,0.08)',
            pt: 1.25,
          }}
        >
          <NotificationsActiveOutlinedIcon sx={{ fontSize: 14 }} />
          {t('social.key.toReactivate', '3 relations à réactiver')}
        </Box>
        <Stack spacing={0.75}>
          {reac.length > 0 ? (
            reac.map((p) => (
              <PersonRow
                key={`reac-${p.id}`}
                person={p}
                badge={
                  p.last_interaction
                    ? `${t('social.key.lastSeen', 'Vu')} ${moment(p.last_interaction).fromNow()}`
                    : t('social.key.neverSeen', 'Pas d’interaction enregistrée')
                }
              />
            ))
          ) : (
            <Box sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
              {t('social.key.noReac', 'Aucune relation à réactiver')}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
